import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router'
import { PatientDetailContextProvider } from '~/features/member-profile'
import { PHIAccessBlock } from '~/shared/security'
import { Box } from '@mui/material'
import { Loader } from '~/shared/ui'
// Lazy load screen components for code splitting
const Members = lazy(() => import('~/features/members-dashboard'))
const MembersQueueDrilldown = lazy(() =>
  import('~/features/members-dashboard').then(m => ({ default: m.MembersQueueDrilldown })),
)
const PatientDetailV2 = lazy(() => import('~/features/member-profile'))
const CaseDetail = lazy(() => import('~/app/CaseDetail'))
const Inbox = lazy(() => import('~/features/inbox'))
const Settings = lazy(() => import('~/features/settings'))
const Worklist = lazy(() => import('~/features/worklist'))
const PersonDetail = lazy(() =>
  import('~/features/member-profile').then(m => ({ default: m.PersonDetail })),
)
const ProviderScheduleFullCalendar = lazy(() =>
  import('~/features/settings/provider-shifts').then(m => ({
    default: m.ProviderScheduleFullCalendar,
  })),
)

// Redirects `/<section>/:id` to the section's default tab, preserving the
// current search parameters.
const DefaultTabRedirect = ({ tab }: { tab: string }) => {
  const { pathname, search } = useLocation()
  return <Navigate to={`${pathname.replace(/\/+$/, '')}/${tab}${search}`} replace />
}

const CaseDetailRoute = () => {
  const { id } = useParams()
  return <CaseDetail id={Number(id)} />
}

const PatientDetailRoute = () => {
  const { id } = useParams()
  return (
    <PatientDetailContextProvider>
      <PHIAccessBlock userId={Number(id)}>
        <PatientDetailV2 />
      </PHIAccessBlock>
    </PatientDetailContextProvider>
  )
}

const PersonDetailRoute = () => {
  const { id } = useParams()
  return <PersonDetail personId={Number(id)} providers={[]} />
}

const InterfaceSwitch = () => {
  const patientProfileTabDestination = 'todos'
  const personProfileTabDestination = 'profile'

  return (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        overflow: 'auto',
      }}
    >
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/cases/:id/*" element={<CaseDetailRoute />} />
          {/*
            `/inbox` and `/worklist` carry no tenant feature-access check. The Tasks API enforces
            the `tasks` feature itself (403), and no worklist or work-unit endpoint enforces
            `worklists` at all, so a check here would hide screens without protecting any data.
            `InterfaceNav` hides both sidebar links behind those features, and `/inbox` is the
            post-login landing route, so a check here would also have to render a loading state
            rather than nothing while feature access resolves.
          */}
          <Route path="/inbox/*" element={<Inbox />} />
          <Route path="/tasks/*" element={<Navigate to="/inbox" replace />} />
          <Route path="/messages/*" element={<Navigate to="/inbox" replace />} />
          <Route path="/members/queues/:key" element={<MembersQueueDrilldown />} />
          <Route path="/members" element={<Members />} />
          <Route path="/worklist" element={<Worklist />} />
          <Route
            path="/patients/:id"
            element={<DefaultTabRedirect tab={patientProfileTabDestination} />}
          />
          <Route path="/patients/:id/*" element={<PatientDetailRoute />} />
          <Route path="/schedule" element={<ProviderScheduleFullCalendar />} />
          <Route
            path="/people/:id"
            element={<DefaultTabRedirect tab={personProfileTabDestination} />}
          />
          <Route path="/people/:id/*" element={<PersonDetailRoute />} />
          <Route
            path="/person/:id"
            element={<DefaultTabRedirect tab={personProfileTabDestination} />}
          />
          <Route path="/person/:id/*" element={<PersonDetailRoute />} />
          <Route path="/settings/*" element={<Settings />} />
        </Routes>
      </Suspense>
    </Box>
  )
}

export default InterfaceSwitch
